import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useLanguage } from "../../context/LanguageContext";
import { tCrm, adminLogin, agentLogin, auth } from "../../lib/crm";

export default function CRMLogin() {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const navigate = useNavigate();

  const [mode, setMode] = useState("agent"); // 'agent' | 'admin'
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth.isAuthed()) navigate("/crm", { replace: true });
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "agent") {
        await agentLogin(email, password);
      } else {
        await adminLogin(username, password);
      }
      navigate("/crm", { replace: true });
    } catch {
      setError(t("login.invalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] px-4">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="glass-strong rounded-2xl p-8 border border-white/10 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_3eb1f7e1-02cf-4acb-8ee2-746b00b03901/artifacts/kuo8ppes_Logo%20Global%20tech%20500x500%20FT.png"
                alt="GTX"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="leading-tight">
              <div className="font-bold text-white text-lg">{t("login.title")}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mono">
                {t("login.subtitle")}
              </div>
            </div>
          </div>

          <div className="flex gap-1 p-1 bg-white/5 rounded-lg">
            <button
              type="button"
              onClick={() => setMode("agent")}
              data-testid="login-tab-agent"
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition ${
                mode === "agent" ? "bg-lime-400 text-white" : "text-white/60 hover:text-white"
              }`}
            >
              {t("login.agentTab")}
            </button>
            <button
              type="button"
              onClick={() => setMode("admin")}
              data-testid="login-tab-admin"
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition ${
                mode === "admin" ? "bg-lime-400 text-white" : "text-white/60 hover:text-white"
              }`}
            >
              {t("login.adminTab")}
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "agent" ? (
              <div>
                <Label htmlFor="email" className="text-white/70">{t("login.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="username" className="text-white/70">{t("login.username")}</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  data-testid="login-username-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            )}
            <div>
              <Label htmlFor="pw" className="text-white/70">{t("login.password")}</Label>
              <Input
                id="pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-lime-400 hover:bg-lime-300 text-white font-semibold"
            >
              {loading ? "…" : t("login.submit")}
            </Button>
          </form>

          <Link to="/" className="block text-center text-xs text-white/40 hover:text-white/70 transition">
            {t("login.backToWeb")}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
